import { useEffect, useState } from 'react'

import {
  restorableDraft,
  sweepExpiredDrafts,
  writeDraft,
} from '../composerDrafts'

// One sweep per page load is enough; every later mount would rescan the same
// keys for nothing.
let hasSweptExpiredDrafts = false

export const useComposerDraft = (
  ticketPublicId: string,
  pendingBodies: readonly string[],
) => {
  const [draft, setDraft] = useState(() =>
    restorableDraft(ticketPublicId, pendingBodies),
  )
  const [restoredFor, setRestoredFor] = useState(ticketPublicId)

  useEffect(() => {
    if (hasSweptExpiredDrafts) return
    hasSweptExpiredDrafts = true
    sweepExpiredDrafts()
  }, [])

  // Switching threads swaps the draft during render rather than in an effect,
  // so the composer never paints one ticket's reply under another's.
  let currentDraft = draft
  if (restoredFor !== ticketPublicId) {
    currentDraft = restorableDraft(ticketPublicId, pendingBodies)
    setRestoredFor(ticketPublicId)
    setDraft(currentDraft)
  }

  const updateDraft = (body: string) => {
    setDraft(body)
    writeDraft(ticketPublicId, body)
  }

  return {
    draft: currentDraft,
    updateDraft,
    // The composer empties at once, but storage holds the exact text that is in
    // flight. A reload during a hung send gets the reply back; only the service
    // confirming it clears the stored copy.
    holdSentDraft: (sentBody: string) => {
      setDraft('')
      writeDraft(ticketPublicId, sentBody)
    },
    // A discarded send never destroys what the user is writing now: it lands
    // under the current draft instead of replacing it.
    restoreDraft: (body: string) =>
      updateDraft(
        currentDraft.trim() ? `${currentDraft.trimEnd()}\n\n${body}` : body,
      ),
  }
}
